import React, { useEffect, useMemo, useState } from "react";
import { getAuth, onAuthStateChanged } from "firebase/auth";
import {
  collection,
  doc,
  getDoc,
  getDocs,
  getFirestore,
  writeBatch,
} from "firebase/firestore";
import app from "../firebase";

const CUERPO_ID = "Huasco";

// Todas las colecciones visibles actualmente en Firestore.
// "migrar: true" = esta herramienta puede agregar cuerpo_id de forma automática.
// "migrar: false" = se analiza, pero NO se modifica automáticamente por seguridad.
const COLECCIONES = [
  { nombre: "administradores", migrar: false, nota: "Global / revisar roles y superadmin antes de asignar cuerpo" },
  { nombre: "auditoria_partes", migrar: true, nota: "Datos operacionales por Cuerpo" },
  { nombre: "bomberos", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "bomberos_en_servicio", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "bomberos_fuera_servicio", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "citaciones", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "companias", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "configuracion", migrar: false, nota: "Revisar documento a documento; hoy contiene configuración global" },
  { nombre: "cuerpos_bomberos", migrar: false, nota: "Colección maestra de Cuerpos; no se migra" },
  { nombre: "external_cache", migrar: false, nota: "Caché global; no se migra" },
  { nombre: "folios_partes", migrar: false, nota: "Ya usa claves separadas por Cuerpo; revisar antes de tocar" },
  { nombre: "jurisdicciones", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "logos", migrar: false, nota: "Analizar primero cómo se usan los logos" },
  { nombre: "operadoras", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "partes_emergencia", migrar: true, nota: "Ya existen documentos con cuerpo_id; completar faltantes" },
  { nombre: "partes_en_creacion", migrar: true, nota: "Datos temporales por Cuerpo" },
  { nombre: "tiposEmergencia", migrar: false, nota: "Analizar primero si será global o configurable por Cuerpo" },
  { nombre: "unidades", migrar: true, nota: "Datos por Cuerpo" },
  { nombre: "usuarios_partes", migrar: true, nota: "Usuarios asociados a Partes por Cuerpo" },
];

const VACIO = {
  total: 0,
  correctos: 0,
  faltantes: 0,
  conflictos: 0,
  conflictosDetalle: [],
  acceso: "pendiente",
  errorAcceso: "",
};

function tieneCuerpoValido(data) {
  return (
    Object.prototype.hasOwnProperty.call(data, "cuerpo_id") &&
    data.cuerpo_id !== null &&
    String(data.cuerpo_id).trim() !== ""
  );
}

export default function MigracionHuasco() {
  const db = useMemo(() => getFirestore(app), []);
  const auth = useMemo(() => getAuth(app), []);

  const [usuario, setUsuario] = useState(null);
  const [rol, setRol] = useState("");
  const [cargandoRol, setCargandoRol] = useState(true);
  const [analizando, setAnalizando] = useState(false);
  const [migrando, setMigrando] = useState(false);
  const [resultado, setResultado] = useState({});
  const [mensaje, setMensaje] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      setUsuario(user || null);
      setRol("");
      setCargandoRol(true);

      if (!user) {
        setCargandoRol(false);
        return;
      }

      try {
        const snap = await getDoc(doc(db, "administradores", user.uid));
        setRol(snap.exists() ? String(snap.data()?.rol || "") : "");
      } catch (e) {
        console.error(e);
        setError("No fue posible verificar el rol del administrador.");
      } finally {
        setCargandoRol(false);
      }
    });

    return () => unsub();
  }, [auth, db]);

  const autorizado = usuario && ["admin", "superadmin"].includes(rol);

  async function analizarColeccion(nombre) {
    const snap = await getDocs(collection(db, nombre));
    const r = { ...VACIO, conflictosDetalle: [] };

    snap.forEach((d) => {
      r.total += 1;
      const data = d.data();

      if (!tieneCuerpoValido(data)) {
        r.faltantes += 1;
        return;
      }

      if (String(data.cuerpo_id).trim() === CUERPO_ID) {
        r.correctos += 1;
        return;
      }

      r.conflictos += 1;
      if (r.conflictosDetalle.length < 30) {
        r.conflictosDetalle.push({ id: d.id, cuerpo_id: data.cuerpo_id });
      }
    });

    r.acceso = "ok";
    return r;
  }

  async function analizarTodo() {
    if (!autorizado) return;

    setAnalizando(true);
    setMensaje("");
    setError("");

    try {
      const nuevo = {};
      const bloqueadas = [];

      for (const item of COLECCIONES) {
        try {
          nuevo[item.nombre] = await analizarColeccion(item.nombre);
        } catch (e) {
          console.error(`No se pudo analizar ${item.nombre}:`, e);
          nuevo[item.nombre] = {
            ...VACIO,
            acceso: "denegado",
            errorAcceso: e?.code || e?.message || String(e),
          };
          bloqueadas.push(item.nombre);
        }
      }

      setResultado(nuevo);
      if (bloqueadas.length) {
        setMensaje(
          `Análisis terminado. No se modificó ningún documento.\n` +
          `Firestore bloqueó la lectura completa de: ${bloqueadas.join(", ")}. ` +
          `Las demás colecciones sí fueron analizadas.`
        );
      } else {
        setMensaje("Análisis terminado. No se modificó ningún documento.");
      }
    } catch (e) {
      console.error(e);
      setError(`Error inesperado durante el análisis: ${e.message || e}`);
    } finally {
      setAnalizando(false);
    }
  }

  async function migrarColeccion(nombre) {
    const snap = await getDocs(collection(db, nombre));
    const pendientes = [];
    const conflictos = [];

    snap.forEach((d) => {
      const data = d.data();

      if (!tieneCuerpoValido(data)) {
        pendientes.push(d.ref);
        return;
      }

      if (String(data.cuerpo_id).trim() !== CUERPO_ID) {
        conflictos.push({ id: d.id, cuerpo_id: data.cuerpo_id });
      }
    });

    for (let i = 0; i < pendientes.length; i += 400) {
      const lote = writeBatch(db);
      const bloque = pendientes.slice(i, i + 400);

      bloque.forEach((ref) => {
        lote.update(ref, { cuerpo_id: CUERPO_ID });
      });

      await lote.commit();
    }

    return {
      actualizados: pendientes.length,
      conflictos: conflictos.length,
      conflictosDetalle: conflictos.slice(0, 30),
    };
  }

  async function ejecutarMigracion() {
    if (!autorizado || migrando) return;

    const migrables = COLECCIONES.filter((x) => x.migrar);
    const confirmar = window.confirm(
      `Se agregará cuerpo_id: "${CUERPO_ID}" SOLO en las colecciones marcadas como migrables y SOLO a documentos sin cuerpo_id.\n\n` +
        "Las colecciones globales o dudosas se analizarán, pero NO se modificarán.\n\n" +
        "¿Deseas continuar?"
    );

    if (!confirmar) return;

    setMigrando(true);
    setMensaje("");
    setError("");

    try {
      const resumen = [];
      for (const item of migrables) {
        const r = await migrarColeccion(item.nombre);
        resumen.push(`${item.nombre}: ${r.actualizados} actualizado(s), ${r.conflictos} conflicto(s)`);
      }

      setMensaje(`Migración terminada.\n${resumen.join("\n")}`);
      await analizarTodo();
    } catch (e) {
      console.error(e);
      setError(`La migración se detuvo por un error: ${e.message || e}`);
    } finally {
      setMigrando(false);
    }
  }

  if (cargandoRol) {
    return <div style={styles.page}>Verificando administrador...</div>;
  }

  if (!usuario) {
    return <div style={styles.page}>Debes iniciar sesión como administrador.</div>;
  }

  if (!autorizado) {
    return (
      <div style={styles.page}>
        <h2>Migración multi-Cuerpo</h2>
        <div style={styles.alertError}>
          Tu usuario no tiene rol admin o superadmin para ejecutar esta migración.
        </div>
      </div>
    );
  }

  const hayAnalisis = Object.keys(resultado).length > 0;
  const migrables = COLECCIONES.filter((x) => x.migrar);
  const totalFaltantesMigrables = migrables.reduce(
    (a, x) => a + (resultado[x.nombre]?.faltantes || 0),
    0
  );
  const totalConflictosMigrables = migrables.reduce(
    (a, x) => a + (resultado[x.nombre]?.conflictos || 0),
    0
  );
  const migrablesSinAcceso = migrables.filter(
    (x) => resultado[x.nombre] && resultado[x.nombre].acceso !== "ok"
  );

  return (
    <div style={styles.page}>
      <h1 style={{ marginTop: 0 }}>Migración multi-Cuerpo · Huasco</h1>
      <p>
        Esta herramienta analiza <strong>todas las colecciones actuales</strong>. Solo modifica automáticamente
        las que están marcadas como <strong>Migrar</strong> y únicamente agrega <code>cuerpo_id: "Huasco"</code>
        cuando el documento todavía no tiene un cuerpo asignado.
      </p>

      <div style={styles.infoBox}>
        <strong>Cuerpo de destino:</strong> Huasco
        <br />
        <strong>Rol:</strong> {rol}
        <br />
        <strong>Colecciones analizadas:</strong> {COLECCIONES.length}
      </div>

      <div style={styles.actions}>
        <button
          type="button"
          onClick={analizarTodo}
          disabled={analizando || migrando}
          style={styles.primaryButton}
        >
          {analizando ? "Analizando..." : "1. Analizar TODAS sin modificar"}
        </button>

        <button
          type="button"
          onClick={ejecutarMigracion}
          disabled={!hayAnalisis || analizando || migrando || totalConflictosMigrables > 0 || migrablesSinAcceso.length > 0}
          style={{
            ...styles.dangerButton,
            opacity: !hayAnalisis || analizando || migrando || totalConflictosMigrables > 0 || migrablesSinAcceso.length > 0 ? 0.5 : 1,
          }}
          title={totalConflictosMigrables > 0 ? "Hay conflictos en colecciones migrables" : migrablesSinAcceso.length > 0 ? "Hay colecciones migrables sin permiso de lectura" : ""}
        >
          {migrando ? "Migrando..." : `2. Agregar cuerpo_id a ${totalFaltantesMigrables} documento(s) migrables`}
        </button>
      </div>

      {mensaje && <pre style={styles.alertOk}>{mensaje}</pre>}
      {error && <pre style={styles.alertError}>{error}</pre>}

      {hayAnalisis && (
        <>
          <h2>Resultado del análisis completo</h2>
          <div style={{ overflowX: "auto" }}>
            <table style={styles.table}>
              <thead>
                <tr>
                  <th style={styles.th}>Colección</th>
                  <th style={styles.th}>Acceso</th>
                  <th style={styles.th}>Acción</th>
                  <th style={styles.th}>Total</th>
                  <th style={styles.th}>Ya Huasco</th>
                  <th style={styles.th}>Sin cuerpo_id</th>
                  <th style={styles.th}>Otro cuerpo_id</th>
                  <th style={styles.th}>Observación</th>
                </tr>
              </thead>
              <tbody>
                {COLECCIONES.map((item) => {
                  const r = resultado[item.nombre] || VACIO;
                  return (
                    <tr key={item.nombre}>
                      <td style={styles.td}><code>{item.nombre}</code></td>
                      <td style={styles.td}>
                        {r.acceso === "ok" ? (
                          <strong>OK</strong>
                        ) : r.acceso === "denegado" ? (
                          <strong style={{ color: "#a40000" }}>SIN PERMISO</strong>
                        ) : (
                          "—"
                        )}
                      </td>
                      <td style={styles.td}>
                        <strong>{item.migrar ? "Migrar" : "Solo analizar"}</strong>
                      </td>
                      <td style={styles.td}>{r.total}</td>
                      <td style={styles.td}>{r.correctos}</td>
                      <td style={styles.td}>{r.faltantes}</td>
                      <td style={styles.td}>{r.conflictos}</td>
                      <td style={styles.td}>{item.nota}{r.errorAcceso ? ` · ${r.errorAcceso}` : ""}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {migrablesSinAcceso.length > 0 && (
            <div style={styles.alertError}>
              <strong>Migración bloqueada por seguridad.</strong> No se pudo leer completamente: {migrablesSinAcceso.map((x) => x.nombre).join(", ")}.
              No se escribirá nada hasta que todas las colecciones marcadas como Migrar puedan analizarse.
            </div>
          )}

          {totalConflictosMigrables > 0 && (
            <div style={styles.alertError}>
              <strong>No ejecutes la migración todavía.</strong> Encontré {totalConflictosMigrables} documento(s)
              con un <code>cuerpo_id</code> distinto de Huasco dentro de colecciones que sí pretendemos migrar.
              El botón queda bloqueado hasta revisarlos.
              {migrables.map((item) => {
                const detalles = resultado[item.nombre]?.conflictosDetalle || [];
                if (!detalles.length) return null;
                return (
                  <div key={item.nombre} style={{ marginTop: 12 }}>
                    <strong>{item.nombre}</strong>
                    <ul>
                      {detalles.map((x) => (
                        <li key={x.id}>
                          {x.id}: cuerpo_id = {String(x.cuerpo_id)}
                        </li>
                      ))}
                    </ul>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
}

const styles = {
  page: {
    maxWidth: 1400,
    margin: "0 auto",
    padding: 28,
    fontFamily: "Arial, sans-serif",
  },
  infoBox: {
    padding: 14,
    border: "1px solid #ccc",
    borderRadius: 8,
    margin: "18px 0",
    background: "#f7f7f7",
  },
  actions: {
    display: "flex",
    gap: 12,
    flexWrap: "wrap",
    marginBottom: 20,
  },
  primaryButton: {
    padding: "12px 18px",
    borderRadius: 8,
    border: 0,
    cursor: "pointer",
    fontWeight: 700,
  },
  dangerButton: {
    padding: "12px 18px",
    borderRadius: 8,
    border: 0,
    cursor: "pointer",
    fontWeight: 700,
  },
  alertOk: {
    whiteSpace: "pre-wrap",
    padding: 14,
    border: "1px solid #a7d7ae",
    borderRadius: 8,
    background: "#eef9f0",
  },
  alertError: {
    whiteSpace: "pre-wrap",
    padding: 14,
    border: "1px solid #e1aaaa",
    borderRadius: 8,
    background: "#fff1f1",
  },
  table: {
    width: "100%",
    borderCollapse: "collapse",
    marginBottom: 20,
    minWidth: 1100,
  },
  th: {
    textAlign: "left",
    padding: 10,
    borderBottom: "2px solid #ccc",
    verticalAlign: "top",
  },
  td: {
    padding: 10,
    borderBottom: "1px solid #ddd",
    verticalAlign: "top",
  },
};
